import React, {useState, useEffect, useRef} from "react";
import { FaTimes as FaClose, FaFilter, FaTimes, FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import server from '../../../api/server.js';
import '../../../dashboard-components/loader.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { navigateWithCurrentQuery } from '../../../utils/routerUtils.js';
import ModernTimeDropdown from "../../../dashboard-components/layouts/modernDropdown.jsx";
import { useToast } from '../../../context/ToastContext.jsx';
import { Download } from 'lucide-react';
import { initializeFiltersFromUrl, updateUrlWithFilters } from '../../../utils/filterUtils.js';
import CreateCampaignModal from "./CreateCampaignModal.jsx";

const CampaignsPage = ({serviceID, clientID, segment, type}) => {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Default filter values
  const defaultFilters = {
    agent: '',
    id: '',
    date: 'Last 7 Days',
    customStart: '',
    customEnd: ''
  };

  const defaultAdvancedFilters = {
    id: ''
  };

  // Initialize filters from URL or use defaults (only when not passed via segment prop)
  const initialFilterState = !segment ? initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters) : {
    filters: defaultFilters,
    advancedFilters: defaultAdvancedFilters,
    customStart: '',
    customEnd: ''
  };

    const [filteredCampaigns, setFilteredCampaigns] = useState([]);
    const [email, setEmail] = useState(null)
    const [client_id, setClient_id] = useState(null)
    // service_id is not used by APIs; rely on route param or provided prop for navigation only
    const [totalRecords, setTotalRecords] = useState(0)
    const [originalSize, setOriginalSize] = useState(0)
    const [openType, setOpenType] = useState(null)
    const [showCampaignsPage, setShowCampaignsPage] = useState(false)
    const [selectedCampaignID, setSelectedCampaignID] = useState(null)
    const [segmentParameterData, setSegmentParameterData] = useState({'type':null, 'id': null, 'name': null})
    const [role] = useState(()=> {
      const userCookie = getCookie('dashboard_login_user_creds');
      if (userCookie) {
        const userData = JSON.parse(userCookie);
        const role = userData.role
        return role
      }
      return 'admin';
    })
    const [filters, setFilters] = useState(initialFilterState.filters);
    const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);
      const [page, setPage] = useState(1);
      const [totalPages, setTotalPages] = useState(1);
      const [apiCallRunning, setApiCallRunning] = useState(false)
      const [loading, setLoading] = useState(false)
      const [showCreateCampaignModal, setShowCreateCampaignModal] = useState(false)
      // Ref for aborting customer page requests
      const campaignsAbortRef = useRef(null);
      const [dummyFilterData, setDummyFilterData] = useState({
        agents: [],
        date: ['Today',
          'Yesterday',
          'This Week',
          "This Month",
          "Last Month",
          "Last 7 Days",
          "Last 30 Days",
          "Last 3 Months",
          "Custom Range"]
      });
      

      function getCookie(name) {
        function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
        var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
        return match ? match[1] : null;
    }

    const handleQuickFilterChange = (e, key) => {
      const value = e.target.value;
      
      // If changing time filter and it's not "Custom Range", clear the custom date values
      if ((key === 'date') && value !== 'Custom Range') {
        setFilters((prev) => ({ 
          ...prev, 
          [key]: value,
          'Custom Start': '',
          'Custom End': ''
        }));
      } else {
        setFilters((prev) => ({ ...prev, [key]: value }));
      }
    };
      const handleAdvancedInputChange = (e, key) => {
        const value = e.target.value;
        setAdvancedFilters((prev) => ({
          ...prev,
          [key]: value,
        }));
      };
    
    const formatEpochToReadableDate = (epoch) => {
        const date = new Date(epoch*1000); // Convert seconds to milliseconds
        const options = { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false };
        return date.toLocaleString('en-GB', options).replace(',', '');
      };

    
    const get_campaigns_page_details = async (client_id, service_id, page_size) => {
      // abort previous
      if(campaignsAbortRef.current){ campaignsAbortRef.current.abort(); }
      const controller = new AbortController();
      campaignsAbortRef.current = controller;

      try {
        setApiCallRunning(true);
        setLoading(true);

        const res = await server.get_voice_agent_campaign_list(client_id, null, email, 'campaigns', undefined, page, filters.id, filters.agent, filters.date, filters['Custom Start'], filters['Custom End']);
        if(res.status === 200){
          const resp = await res.json();
          setFilteredCampaigns(resp.data);
          const totalRecords = resp.total_records;
          setTotalRecords(totalRecords);
          setOriginalSize(totalRecords);
          setTotalPages(Math.ceil(totalRecords / 50));
          setLoading(false);
        }else{
          showToast('Something went wrong. Please try again later', 'error')
          setApiCallRunning(false)
          setLoading(false)
        }
        setApiCallRunning(false);
      }catch(error){
        if(error.name === 'AbortError'){
          setApiCallRunning(false);
          return;
        }
        setApiCallRunning(false);
        setLoading(false);
      }
    }

  // fetch filter details once
  const fetchFilterDetailsOnceCampaigns = async () => {
    try{
      const res = await server.getVoiceAgentsMetricsFilters(client_id, null, email, 'campaigns', undefined);
      if(res.status === 200){
        const data = await res.json();
        setDummyFilterData(prev=>({...prev, agents: data.agents}));
      }
    }catch(err){}
  }

  const onCreateCampaign = () => {
    setShowCreateCampaignModal(false)
    get_campaigns_page_details(client_id, null, 1);
  }

  useEffect(()=>{
    if(client_id && email){
      fetchFilterDetailsOnceCampaigns();
    }
  }, [client_id, email]);
    
      const handleNextPage = () => {
        if (page < totalPages) {
            const newPage = page + 1;
            setPage(newPage); 
            get_campaigns_page_details(client_id, null, newPage); // ✅ Ensure only one call
        }
      };
    
      const handlePreviousPage = () => {
          if (page > 1) {
              const newPage = page - 1;
              setPage(newPage);
              get_campaigns_page_details(client_id, null, newPage); // ✅ Ensure only one call
          }
      };
      const isInitializingRef = useRef(false); // to prevent API calls during initialization
    
      useEffect(()=> {
        isInitializingRef.current = true;
        const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
          if (user) {
            setEmail(user.email);
          }
          // serviceID comes from props if needed for navigation; APIs ignore service_id
          if(clientID){
            setClient_id(clientID)
          }
          if(type){
            setOpenType(type)
          }
          const initTimeout = setTimeout(() => {
            isInitializingRef.current = false;
          }, 100);
          
          return () => {
            clearTimeout(initTimeout);
          };
      }, [serviceID, clientID, role])

      // Handle page navigation
      useEffect(() => {
          if(client_id){
            get_campaigns_page_details(client_id, null, page);
          }
        }, [client_id, page]);
      // Debounce API calls when filters change to avoid rapid requests while typing
      const filtersDebounceRef = useRef(null);
      const firstFiltersLoadRef = useRef(true);
      useEffect(() => {
        if (!client_id) return;
        // Skip debounce logic on initial mount to avoid duplicate first fetch
        if (firstFiltersLoadRef.current) {
          firstFiltersLoadRef.current = false;
          return;
        }
        // Skip API calls during initialization to prevent multiple calls on page reload
        if (isInitializingRef.current) {
          return;
        }
        // Clear previous debounce timer
        if (filtersDebounceRef.current) {
          clearTimeout(filtersDebounceRef.current);
        }
        filtersDebounceRef.current = setTimeout(() => {
          // Only reset to first page; page effect will handle fetching once
          if (page !== 1) {
            setPage(1);
          } else {
              get_campaigns_page_details(client_id, null, 1);
          }
        }, 600); // idle delay

        return () => {
          if (filtersDebounceRef.current) {
            clearTimeout(filtersDebounceRef.current);
          }
        };
      }, [filters, advancedFilters, client_id]);

        const handleCampaignsPage = (id) => {
          if (openType === 'modal-open'){
            setSelectedCampaignID(id)
            setShowCampaignsPage(true);
            return;
          }
          // Properly encode the customer ID to handle special characters, spaces, and other URL-unsafe characters
          const encodedCampaignId = encodeURIComponent(id);
          const sid = serviceID || 'DEFAULT';
          navigateWithCurrentQuery(navigate, location, `/voice-agents/${client_id}/${sid}/campaigns/${encodedCampaignId}`)
        }
    
    return (
        <div className="flex flex-col font-sans bg-white" style={{height: "100%"}}>
          {!showCampaignsPage && <main className="flex-1 py-3 px-5 bg-gray-50">
            <div className="mb-4">
                <h1 className="text-xl font-semibold text-gray-900 mb-2">Campaigns</h1>
                <p className="text-sm text-gray-500">View and manage all campaigns</p>
            </div>

            {/* Active Filters */}
            {advancedFilters.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex flex-wrap gap-2">
                    {advancedFilters.map((filter, index) => (
                    <div key={index} className="flex items-center bg-gray-100 rounded-full px-3 py-1 text-sm">
                        <span className="mr-1">{filter.type}:</span>
                        <span className="font-medium">{filter.value}</span>
                        <button 
                        className="ml-2 text-gray-500 hover:text-gray-700"
                        >
                        <FaClose className="h-3 w-3" />
                        </button>
                    </div>
                    ))}
                    {advancedFilters.length > 0 && (
                    <button 
                        className="text-sm text-blue-600 hover:text-blue-800"
                        onClick={() => setAdvancedFilters([])}
                    >
                        Clear all
                    </button>
                    )}
                </div>
                </div>
            )}

            {/* Primary Filter Section */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                <div className="flex justify-between items-center mb-4">
                <div className="text-sm font-medium text-gray-700">Quick Filters</div>
                <div className="flex space-x-3">
                <button onClick={()=>setShowCreateCampaignModal(true)} className="flex items-center px-4 py-2 rounded text-sm text-white hover:opacity-90" style={{ backgroundColor: "rgb(79, 70, 229)" }}>Create Campaign</button>
                </div>
                </div>
                <div className="grid grid-cols-5 gap-4">
              
                    <input class="global-input"value={filters.id} type="text" placeholder="Campaign ID" onChange={(e) => handleQuickFilterChange(e, 'id')} 
                    className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input" />
                
                <ModernTimeDropdown isDisabled={role === 'agent'} placeholder="Agent" value={filters.agent} onChange={(e) => handleQuickFilterChange(e, 'agent')} options={dummyFilterData.agents}/>
                </div>
                {/* Filter Display */}
                <div className="flex gap-4 mb-4 mt-4 flex-wrap">
                    {Object.keys(filters).map(
                      (key) => {
                        if (key === 'date') {
                          return null;
                        }
                        if ((key === 'Custom Start' || key === 'Custom End') && filters.date !== 'Custom Range') {
                          return null;
                        }
                        return filters[key] && (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {filters[key]}</span>
                            {(key!=='date' && key !== segmentParameterData.id && !(role === 'employee' && key === 'employee')) && <button
                              className="ml-2 text-gray-600"
                              onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>}
                          </div>
                        )
                      })}
                      {/* Show advanced filters that are set (excluding defaults) */}
                      {Object.entries(advancedFilters).map(([key, val]) => {
                        // skip if blank or default
                        if (
                          val === ''
                        ) {
                          return null;
                        }
                        return (
                          <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                            <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
                            <button
                              className="ml-2 text-gray-600"
                              onClick={() => setAdvancedFilters((prev) => ({ ...prev, [key]: '' }))}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        );
                      })}
                    </div>
            </div>

            {
              showCreateCampaignModal && (
                <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
                        <div style={{position: "fixed", left: "17%", top: "15%", width: "66%", height: "70%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
                        zIndex: "999999999"}}>
                          <div className="flex items-center justify-between px-4 pt-4">
                            <h2 className="text-lg font-medium text-gray-900">Create Campaign</h2>
                            <div style={{position: "absolute", right: "10px", top: "10px", cursor: "pointer"}} onClick={()=>{setShowCreateCampaignModal(false);}}><FaTimes style={{color: "slategray"}}/></div>
                          </div>
                            <div style={{overflow: "auto", height: "100%", borderRadius: "10px"}}>
                              <CreateCampaignModal onCreateCampaign={onCreateCampaign} client_id={client_id} email={email} />
                            </div>
                        </div>
                    </div>
              )
            }

            

            {/* Table */}
            <div className="bg-white rounded-lg shadow-sm" style={{height: "52vh", overflowY: "auto"}}>
                {
                    loading ? (
                        <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                            <div className="loader"></div>
                            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead style={{ position: "sticky", top: "0" }}>
                                <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Campaign ID</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Campaign Name</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Agent ID</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Created By</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Created At</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{background: "white", fontSize: "13px"}}>Action</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCampaigns.map((campaign) => (
                                <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-blue-600" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{campaign.campaign_id}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{campaign.campaign_name}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{campaign.agent}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-900" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{campaign.is_active ? 'Active' : 'Inactive'}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{campaign.created_by}</td>
                                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500" style={{fontSize: "0.7rem", lineHeight: "1rem"}}>{formatEpochToReadableDate(campaign.createdat)}</td>
                                    <td onClick={()=>handleCampaignsPage(campaign.campaign_id)} className="px-5 py-3 whitespace-nowrap text-sm text-blue-600" style={{fontSize: "0.7rem", lineHeight: "1rem", cursor: "pointer"}}>View Details</td>
                                </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                }
            </div>
            {/* Pagination */}
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <div onClick={handlePreviousPage} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Previous
                </div>
                <div onClick={handleNextPage} className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50">
                  Next
                </div>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(50*(page-1))+1}</span> to <span className="font-medium">{totalRecords <= 50 ? totalRecords : 50*page}</span> of <span className="font-medium">{totalRecords}</span> records
                  </p>
                </div>
                <div>
                  <nav className="z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <div onClick={handlePreviousPage} className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                      Previous
                    </div>
                    <div onClick={handleNextPage} className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" style={{cursor: "pointer"}}>
                      Next
                    </div>
                  </nav>
                </div>
              </div>
            </div>
            </main>}
        <style jsx>
            {`
            .sections-headings {
                            cursor: pointer;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            padding: 15px;
                            border-radius: 10px;
                            transition: background 0.3s;
                            width: 100%;
                            }

                            .section-title {
                            font-size: 15px;
                            font-weight: 500;
                            display: flex;
                            align-items: center;
                            }
            `}              
          </style>
        </div>
    )
}

export default CampaignsPage